<template>
  <div class="salon-app salon-review">
    <!-- Top app bar + booking progress -->
    <header class="salon-topbar">
      <div class="salon-topbar-row">
        <button class="salon-back" type="button" :aria-label="$t('salon.common.goBack')" @click="$emit('back')">
          <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <h1 class="salon-topbar-title">{{ $t("salon.review.title") }}</h1>
        <SalonProfileMenu
          :initials="memberInitials"
          :name="memberName"
          @navigate="$emit('profile', $event)"
        />
      </div>
      <div class="salon-progress">
        <div class="salon-progress-track">
          <div class="salon-progress-fill" :style="{ width: progressWidth }"></div>
        </div>
        <span class="salon-progress-label">{{ $t("salon.review.step", { step, total: totalSteps }) }}</span>
      </div>
    </header>

    <main class="salon-main">
      <h2 class="salon-review-title">{{ $t("salon.review.heading") }}</h2>

      <!-- Card 1 — where, when, and with whom -->
      <section class="salon-card salon-summary">
        <div class="salon-summary-outlet">
          <span class="salon-summary-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M4 9h16l-1-4H5L4 9zm0 0v10a1 1 0 001 1h14a1 1 0 001-1V9"
                fill="none" stroke="currentColor" stroke-width="1.8"
                stroke-linecap="round" stroke-linejoin="round" />
              <path d="M9 20v-6h6v6" fill="none" stroke="currentColor" stroke-width="1.8"
                stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </span>
          <div class="salon-summary-outlet-body">
            <h3 class="salon-summary-name">{{ branchName }}</h3>
            <p v-if="addressLines.length" class="salon-summary-address">
              <span v-for="(line, index) in addressLines" :key="index">
                {{ line }}<br v-if="index < addressLines.length - 1" />
              </span>
            </p>
          </div>
        </div>

        <div class="salon-summary-rows">
          <div v-for="row in summaryRows" :key="row.key" class="salon-summary-row">
            <div class="salon-summary-fact">
              <span class="salon-summary-fact-icon" aria-hidden="true" v-html="row.icon"></span>
              <span class="salon-summary-fact-text">{{ row.text }}</span>
            </div>
            <button class="salon-edit" type="button" @click="$emit('edit', row.step)">
              {{ $t("salon.review.edit") }}
            </button>
          </div>
        </div>
      </section>

      <!-- Card 2 — what was chosen, and what it comes to -->
      <section class="salon-card salon-services">
        <h3 class="salon-card-title">{{ $t("salon.review.services") }}</h3>

        <!-- SH032 — with prices off, this list is the last thing in the card, so it
             gives up the rule that used to separate it from the totals below. -->
        <div class="salon-service-lines" :class="{ 'salon-service-lines--last': !showPrice }">
          <template v-for="line in serviceLines" :key="line.key">
            <div class="salon-service-line">
              <span class="salon-service-name">{{ line.name }}</span>
              <span v-if="showPrice" class="salon-service-price">{{ formatPrice(line.price) }}</span>
            </div>
            <!-- Add-ons read as part of the service above them, not as their own purchase. -->
            <div
              v-for="addOn in line.add_ons"
              :key="line.key + '-' + addOn.key"
              class="salon-service-line salon-service-line--addon"
            >
              <span class="salon-service-name">+ {{ addOn.name }}</span>
              <span v-if="showPrice" class="salon-service-price">{{ formatPrice(addOn.price) }}</span>
            </div>
            <p v-if="line.note" class="salon-service-note">{{ line.note }}</p>
          </template>
        </div>

        <!-- SH032 — the money block is one statement. Showing a Total over hidden line
             prices would quote the same number the outlet asked to keep off this screen,
             so subtotal, voucher, GST and Total go together. -->
        <div v-if="showPrice" class="salon-totals">
          <div class="salon-total-row">
            <span>{{ $t("salon.review.subtotal") }}</span>
            <span>{{ formatPrice(subtotal) }}</span>
          </div>
          <div v-if="discountAmount > 0" class="salon-total-row salon-total-row--discount">
            <span>{{ promocodeName || $t("salon.review.voucher") }}</span>
            <span>-{{ formatPrice(discountAmount) }}</span>
          </div>
          <div class="salon-total-row">
            <span>{{ $t("salon.review.gst", { rate: gstRate }) }}</span>
            <span>{{ formatPrice(gstAmount) }}</span>
          </div>
        </div>

        <div v-if="showPrice" class="salon-grand-total">
          <span class="salon-grand-total-label">{{ $t("salon.review.total") }}</span>
          <span class="salon-grand-total-value">{{ formatPrice(totalAmount) }}</span>
        </div>
      </section>

      <!-- Card 3 — the voucher.
           Empty, it is one button into the picker. Once a voucher is on the booking the
           card becomes the voucher itself: an x to take it off, and a Change voucher link
           to swap it — removing must not be the only way to pick a different one. -->
      <section v-if="promocodeName" class="salon-card salon-promo salon-promo--applied">
        <div class="salon-promo-row">
          <span class="salon-promo-left">
            <span class="salon-promo-icon salon-promo-icon--on" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 000-4V8z"
                  fill="none" stroke="currentColor" stroke-width="1.8"
                  stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14 7v10" fill="none" stroke="currentColor" stroke-width="1.8"
                  stroke-linecap="round" stroke-dasharray="2 3" />
              </svg>
            </span>
            <span class="salon-promo-applied">
              <span class="salon-promo-name">{{ promocodeName }}</span>
              <span v-if="showPrice && discountAmount > 0" class="salon-promo-saving">
                {{ $t("salon.review.savingApplied", { amount: formatPrice(discountAmount) }) }}
              </span>
            </span>
          </span>
          <button
            class="salon-promo-remove"
            type="button"
            :aria-label="$t('salon.review.removeVoucher')"
            @click="$emit('remove-promo')"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2"
                stroke-linecap="round" />
            </svg>
          </button>
        </div>
        <button class="salon-promo-change" type="button" @click="$emit('promo')">
          {{ $t("salon.review.changeVoucher") }}
        </button>
      </section>

      <button v-else class="salon-card salon-promo" type="button" @click="$emit('promo')">
        <span class="salon-promo-left">
          <span class="salon-promo-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 000-4V8z"
                fill="none" stroke="currentColor" stroke-width="1.8"
                stroke-linecap="round" stroke-linejoin="round" />
              <path d="M14 7v10" fill="none" stroke="currentColor" stroke-width="1.8"
                stroke-linecap="round" stroke-dasharray="2 3" />
            </svg>
          </span>
          <span class="salon-promo-text">{{ $t("salon.review.applyPromo") }}</span>
        </span>
        <span class="salon-promo-chevron" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </button>

      <!-- Card 4 — the cancellation policy the Confirm button agrees to -->
      <section class="salon-policy">
        <span class="salon-policy-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6" />
            <path d="M12 11v5M12 8h.01" fill="none" stroke="currentColor" stroke-width="1.8"
              stroke-linecap="round" />
          </svg>
        </span>
        <p class="salon-policy-text">
          {{ $t("salon.review.policy") }}
        </p>
      </section>
    </main>

    <nav class="salon-cta">
      <!-- The refusal belongs beside the button that caused it: the customer has just
           tapped Confirm at the bottom of a long page, and a message left up in the canvas
           sits behind this bar where they will never see it. -->
      <p v-if="errorMessage" class="salon-error" role="alert">{{ errorMessage }}</p>
      <p v-else class="salon-cta-note">{{ $t("salon.review.payNote") }}</p>
      <button class="salon-cta-btn" type="button" :disabled="isSaving" @click="confirm">
        {{ isSaving ? $t("salon.review.confirming") : $t("salon.review.confirm") }}
      </button>
    </nav>

    <material-loading :active="isSaving" />
  </div>
</template>

<script>
import MaterialLoading from "@/components/MaterialLoading.vue";
import SalonBookingService from "@/services/salonBooking.service";
import { SALON_GST_RATE } from "@/config/const";
import SalonProfileMenu from "@/salon/components/ProfileMenu.vue";
import "@/salon/styles/tokens.css";

// SH009 is the last of the nine salon booking steps (Salon_UI/review_booking).
const STEP = 9;
const TOTAL_STEPS = 9;

const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// SH046 — Chinese renders dates as "2026年9月14日 周一". Every other locale keeps the English text.
const ZH_WEEKDAYS = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];

const ICON_CALENDAR =
  '<svg viewBox="0 0 24 24" width="22" height="22">' +
  '<rect x="3" y="5" width="18" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
  '<path d="M3 10h18M8 3v4M16 3v4" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
  "</svg>";

const ICON_CLOCK =
  '<svg viewBox="0 0 24 24" width="22" height="22">' +
  '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
  '<path d="M12 7.5V12l3 2" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>' +
  "</svg>";

const ICON_PERSON =
  '<svg viewBox="0 0 24 24" width="22" height="22">' +
  '<circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
  '<path d="M4.5 20a7.5 7.5 0 0115 0" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>' +
  "</svg>";

export default {
  name: "SalonReviewBooking",
  components: {
    SalonProfileMenu,
    MaterialLoading,
  },
  props: {
    // SH016 — the avatar in the top bar, and who it stands for.
    memberInitials: {
      type: String,
      default: "",
    },
    memberName: {
      type: String,
      default: "",
    },
    branchCode: {
      type: String,
      default: "",
    },
    branchName: {
      type: String,
      default: "",
    },
    addressText: {
      type: String,
      default: "",
    },
    postalCode: {
      type: String,
      default: "",
    },
    services: {
      type: Array,
      default: () => [],
    },
    date: {
      type: String,
      default: "",
    },
    dateText: {
      type: String,
      default: "",
    },
    time: {
      type: String,
      default: "",
    },
    timeText: {
      type: String,
      default: "",
    },
    durationMin: {
      type: Number,
      default: 0,
    },
    staffId: {
      type: [Number, String],
      default: "",
    },
    staffName: {
      type: String,
      default: "",
    },
    // The voucher picked on the promo screen, if any. The API clamps the amount to the
    // subtotal, so this is a preview of the discount rather than the last word on it.
    promocodeId: {
      type: [Number, String],
      default: "",
    },
    promocodeName: {
      type: String,
      default: "",
    },
    promocodeDiscount: {
      type: Number,
      default: 0,
    },
    // SH032 — minty_branches.salon_show_service_price, carried down from the outlet the
    // customer picked at step 1. False keeps every price off this screen; the booking
    // still carries the amounts, and the ticket after Confirm still prints them.
    showPrice: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["back", "edit", "promo", "remove-promo", "created", "profile"],
  data() {
    return {
      step: STEP,
      totalSteps: TOTAL_STEPS,
      gstRate: SALON_GST_RATE,
      isSaving: false,
      errorMessage: "",
    };
  },
  computed: {
    progressWidth() {
      return ((STEP / TOTAL_STEPS) * 100).toFixed(2) + "%";
    },

    // "930 Yishun Ave 2, #01-123" then "Singapore 769098", the way the reference splits it.
    addressLines() {
      const lines = [];

      if (this.addressText) {
        lines.push(this.addressText);
      }

      if (this.postalCode) {
        lines.push(this.$t("salon.review.postalLine", { postal: this.postalCode }));
      }

      return lines;
    },

    // Flattened for the template, and keyed: two identical services can sit in one basket
    // and index alone would let Vue reuse the wrong row when one of them is edited away.
    serviceLines() {
      return this.services.map((service, index) => ({
        key: (service.id ?? "line") + "-" + index,
        name: service.name || "",
        price: Number(service.price || 0),
        note: (service.note || "").trim(),
        add_ons: (service.add_ons || []).map((addOn, addOnIndex) => ({
          key: (addOn.id ?? "addon") + "-" + addOnIndex,
          name: addOn.name || "",
          price: Number(addOn.price || 0),
        })),
      }));
    },

    subtotal() {
      return this.serviceLines.reduce((total, line) => {
        const addOns = line.add_ons.reduce((sum, addOn) => sum + addOn.price, 0);

        return total + line.price + addOns;
      }, 0);
    },

    // A voucher can never hand money back. The API applies the same clamp, so the screen
    // and the receipt cannot disagree about it.
    discountAmount() {
      return Math.min(Math.max(Number(this.promocodeDiscount || 0), 0), this.subtotal);
    },

    // GST on the discounted subtotal, so a voucher takes the tax down with it rather than
    // the customer paying tax on money they saved.
    gstAmount() {
      return this.round2((this.subtotal - this.discountAmount) * (this.gstRate / 100));
    },

    totalAmount() {
      return this.round2(this.subtotal - this.discountAmount + this.gstAmount);
    },

    /**
     * "Tuesday, 15 September 2026".
     *
     * Spelled out in full here, unlike the "Tue, 15 Sep" the pickers use: those labels sit
     * on chips with no room, while this is the line the customer reads before agreeing to
     * the booking, and an abbreviated month is exactly where a misread happens.
     *
     * Built from `date` rather than reformatting `dateText`, and falling back to it when
     * the date is missing or unparseable.
     */
    dateLongText() {
      if (!this.date) {
        return this.dateText;
      }

      const [year, month, day] = this.date.split("-").map(Number);

      if ([year, month, day].some((part) => Number.isNaN(part))) {
        return this.dateText;
      }

      const parsed = new Date(year, month - 1, day);

      if (this.$i18n.locale === "zh") {
        return year + "年" + month + "月" + day + "日 " + ZH_WEEKDAYS[parsed.getDay()];
      }

      return (
        WEEKDAYS[parsed.getDay()] + ", " + day + " " + MONTHS[month - 1] + " " + year
      );
    },

    // "11:00 AM to 11:53 AM" — the end is the start plus everything that was chosen.
    timeRangeText() {
      if (!this.timeText) {
        return "";
      }

      const start = this.startTimeText();
      const end = this.endTimeText();

      return end ? this.$t("salon.review.timeRange", { start, end }) : start;
    },

    // 0 is "No Preference", a real choice on SH008 rather than a missing stylist.
    stylistText() {
      return this.staffName || this.$t("salon.review.noPreference");
    },

    summaryRows() {
      return [
        { key: "date", step: "date", icon: ICON_CALENDAR, text: this.dateLongText },
        { key: "time", step: "time", icon: ICON_CLOCK, text: this.timeRangeText },
        { key: "staff", step: "staff", icon: ICON_PERSON, text: this.stylistText },
      ].filter((row) => row.text);
    },
  },
  methods: {
    round2(value) {
      return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
    },

    formatPrice(value) {
      return "$" + Number(value || 0).toFixed(2);
    },

    // SH046 — "11:00 AM" in English, "上午11:00" in Chinese.
    formatClock(hours, minutes) {
      const displayHour = hours % 12 === 0 ? 12 : hours % 12;
      const clock = displayHour + ":" + String(minutes).padStart(2, "0");

      if (this.$i18n.locale === "zh") {
        return (hours >= 12 ? "下午" : "上午") + clock;
      }

      return clock + " " + (hours >= 12 ? "PM" : "AM");
    },

    // SH046 — `timeText` arrives pre-formatted in English, so Chinese rebuilds it from `time`.
    startTimeText() {
      if (this.$i18n.locale !== "zh" || !this.time) {
        return this.timeText;
      }

      const [hour, minute] = this.time.split(":").map(Number);

      return [hour, minute].some((part) => Number.isNaN(part))
        ? this.timeText
        : this.formatClock(hour, minute);
    },

    /**
     * The appointment's finishing time, built from the chosen start and duration.
     *
     * Assembled off `date` rather than parsed out of `timeText`: Safari refuses
     * "11:00 AM" outright, and a date-only string would land the arithmetic on a
     * different day whenever an appointment runs past midnight.
     */
    endTimeText() {
      if (!this.date || !this.time || !this.durationMin) {
        return "";
      }

      const [hour, minute] = this.time.split(":").map(Number);
      const [year, month, day] = this.date.split("-").map(Number);

      if ([hour, minute, year, month, day].some((part) => Number.isNaN(part))) {
        return "";
      }

      const end = new Date(year, month - 1, day, hour, minute + this.durationMin);

      return this.formatClock(end.getHours(), end.getMinutes());
    },

    async confirm() {
      if (this.isSaving) {
        return;
      }

      this.isSaving = true;
      this.errorMessage = "";

      try {
        const response = await SalonBookingService.createBooking({
          branch_code: this.branchCode,
          date: this.date,
          time: this.time,
          duration_min: this.durationMin,
          staff_id: Number(this.staffId) || 0,
          staff_name: this.staffName,
          services: this.services.map((service) => ({
            id: service.id,
            name: service.name,
            price: Number(service.price || 0),
            duration_min: Number(service.duration_min || 0),
            note: service.note || "",
            add_ons: (service.add_ons || []).map((addOn) => ({
              id: addOn.id,
              name: addOn.name,
              price: Number(addOn.price || 0),
              duration_min: Number(addOn.duration_min || 0),
            })),
          })),
          promocode_id: this.promocodeId || undefined,
          promocode_name: this.promocodeName || undefined,
          discount_amount: this.discountAmount || undefined,
        });

        if (response.data.result.isSuccess) {
          this.$emit("created", response.data.data.booking);
          return;
        }

        // The slot may have gone while the customer was reading this screen, and the API
        // says which — that message is more useful than a generic failure.
        this.errorMessage =
          response.data.result.message || this.$t("salon.review.confirmError");
      } catch (error) {
        this.errorMessage =
          this.$t("salon.review.confirmNetworkError");
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Tokens come from src/salon/styles/tokens.css, namespaced under .salon-app. */
.salon-review {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  max-width: var(--salon-max-width);
  margin: 0 auto;
  background: var(--salon-background);
  color: var(--salon-on-background);
  font-family: var(--salon-font-body);
  font-size: 14px;
  line-height: 20px;
}

/* ── Top app bar ───────────────────────────────────────────────── */
.salon-topbar {
  position: sticky;
  top: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  padding: 16px var(--salon-container-padding) 8px;
  background: var(--salon-surface);
}

.salon-topbar-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.salon-back {
  position: absolute;
  left: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  color: var(--salon-primary);
  background: transparent;
  border: 0;
  cursor: pointer;
}

.salon-back:active {
  transform: scale(0.95);
}

/* The last step titles its bar in the body face, not the display one — the reference
   gives the headline below it the weight instead. */
.salon-topbar-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-progress {
  margin-top: 12px;
  text-align: center;
}

.salon-progress-track {
  width: 100%;
  height: 3px;
  overflow: hidden;
  background: var(--salon-surface-variant);
  border-radius: 999px;
}

.salon-progress-fill {
  height: 100%;
  background: var(--salon-primary-container);
  border-radius: 999px;
  transition: width 0.5s ease-out;
}

.salon-progress-label {
  display: inline-block;
  margin-top: 8px;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Canvas ────────────────────────────────────────────────────── */
.salon-main {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--salon-stack-md);
  padding: var(--salon-stack-md) var(--salon-container-padding) 168px;
}

/* Inter, not the display face the earlier steps title with. The reference asks for
   `font-headline-lg-mobile`, which its own Tailwind config never defines a family for, so
   it falls back to the body font — and the screenshot confirms a sans headline here while
   "Total" below it stays serif. */
.salon-review-title {
  margin: 8px 0 0;
  font-family: var(--salon-font-body);
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  letter-spacing: -0.01em;
  color: var(--salon-primary);
}

.salon-card {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
  padding: 16px;
  background: var(--salon-surface-lowest);
  border: 0;
  border-radius: var(--salon-radius-card);
  box-shadow: var(--salon-shadow-soft);
}

/* material-dashboard forces Roboto Slab onto every h1-h3 globally, and that beats a scoped
   rule that simply omits font-family. Every heading in this file therefore names its own
   face — see the MTH027 PaymentSuccess reskin for the same trap. */
.salon-card-title {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-primary);
}

/* ── Card 1: summary ───────────────────────────────────────────── */
.salon-summary-outlet {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--salon-surface-variant);
}

.salon-summary-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: var(--salon-on-surface);
  background: var(--salon-surface-variant);
  border-radius: 12px;
}

.salon-summary-outlet-body {
  min-width: 0;
}

.salon-summary-name {
  margin: 0;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-summary-address {
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-summary-rows {
  display: flex;
  flex-direction: column;
  gap: var(--salon-stack-md);
}

.salon-summary-row {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.salon-summary-fact {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.salon-summary-fact-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: var(--salon-on-surface-variant);
}

.salon-summary-fact-text {
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-edit {
  flex-shrink: 0;
  padding: 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-on-surface-variant);
  text-decoration: underline;
  text-decoration-color: var(--salon-surface-variant);
  background: transparent;
  border: 0;
  cursor: pointer;
  transition: color 0.2s ease;
}

.salon-edit:hover {
  color: var(--salon-primary);
}

/* ── Card 2: services & pricing ────────────────────────────────── */
.salon-service-lines {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--salon-surface-variant);
}

.salon-service-line {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
}

.salon-service-name {
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-service-price {
  flex-shrink: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-primary);
}

.salon-service-line--addon .salon-service-name,
.salon-service-line--addon .salon-service-price {
  color: var(--salon-on-surface-variant);
}

.salon-service-note {
  margin: -4px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--salon-on-surface-variant);
}

.salon-service-lines--last {
  padding-bottom: 0;
  border-bottom: 0;
}

.salon-totals {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--salon-surface-variant);
}

.salon-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-on-surface-variant);
}

.salon-total-row--discount {
  color: var(--salon-secondary);
}

.salon-grand-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
}

.salon-grand-total-label {
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-primary);
}

.salon-grand-total-value {
  font-family: var(--salon-font-headline);
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--salon-secondary);
}

/* ── Card 3: promo ─────────────────────────────────────────────── */
.salon-promo {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.salon-promo:hover {
  background: var(--salon-surface-container-low);
}

.salon-promo:active {
  transform: scale(0.99);
}

.salon-promo-left {
  display: flex;
  gap: 12px;
  align-items: center;
  min-width: 0;
}

.salon-promo-icon {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--salon-primary);
  background: var(--salon-surface-variant);
  border-radius: 999px;
}

.salon-promo-text {
  overflow: hidden;
  font-size: 14px;
  line-height: 20px;
  color: var(--salon-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-promo-chevron {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  color: var(--salon-on-surface-variant);
}

/* ── Card 3, once a voucher is on the booking ──────────────────── */
.salon-promo--applied {
  flex-direction: column;
  gap: 12px;
  align-items: stretch;
  cursor: default;
  border: 1.5px solid var(--salon-gold);
}

.salon-promo--applied:hover {
  background: var(--salon-surface-lowest);
}

.salon-promo-row {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
}

.salon-promo-icon--on {
  color: #241a00;
  background: rgba(254, 210, 85, 0.25);
}

.salon-promo-applied {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.salon-promo-name {
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.salon-promo-saving {
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-secondary);
}

.salon-promo-remove {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--salon-on-surface-variant);
  background: var(--salon-surface-container);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: color 0.2s ease, background-color 0.2s ease;
}

.salon-promo-remove:hover {
  color: var(--salon-error);
  background: rgba(186, 26, 26, 0.08);
}

.salon-promo-remove:active {
  transform: scale(0.94);
}

.salon-promo-change {
  padding: 8px 0 0;
  font-family: var(--salon-font-body);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: var(--salon-primary);
  text-align: center;
  background: transparent;
  border: 0;
  border-top: 1px solid var(--salon-surface-variant);
  cursor: pointer;
}

.salon-promo-change:active {
  opacity: 0.7;
}

/* ── Card 4: policy ────────────────────────────────────────────── */
.salon-policy {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 16px;
  margin-top: 8px;
  background: var(--salon-surface-container);
  border: 1px solid var(--salon-surface-variant);
  border-radius: var(--salon-radius-card);
}

.salon-policy-icon {
  display: flex;
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--salon-on-surface-variant);
}

.salon-policy-text {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
}

/* ── Bottom action bar ─────────────────────────────────────────── */
.salon-cta {
  position: fixed;
  bottom: 0;
  left: 50%;
  z-index: 50;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: var(--salon-max-width);
  padding: 16px var(--salon-container-padding)
    calc(20px + env(safe-area-inset-bottom, 0px));
  background: var(--salon-surface-lowest);
  transform: translateX(-50%);
  box-shadow: 0 -4px 20px rgba(28, 28, 30, 0.04);
}

.salon-cta-note {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: var(--salon-on-surface-variant);
  text-align: center;
}

.salon-error {
  margin: 0;
  font-size: 13px;
  line-height: 18px;
  color: var(--salon-error);
  text-align: center;
}

.salon-cta-btn {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 16px;
  font-family: var(--salon-font-body);
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  color: var(--salon-on-primary);
  background: var(--salon-primary-container);
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.salon-cta-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.salon-cta-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.salon-cta-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
